// src/Store/labtechSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { setLoading } from './LoadingSlice';
import BaseUrl from '../../BaseUrl';

const API_BASE_URL = BaseUrl.labsurl;

// Get auth token from localStorage
const getAuthToken = () => {
  return localStorage.getItem('accessToken');
};

// Create axios instance with default config
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'text/plain'
  }
});

// Add request interceptor to add auth token
apiClient.interceptors.request.use(
  (config) => {
    const token = getAuthToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// ----- Services -----
export const fetchServices = createAsyncThunk(
  'labtech/fetchServices',
  async (_, { rejectWithValue, dispatch }) => {
    try {
      dispatch(setLoading(true));
      const response = await apiClient.get('/LabTestServices');
      dispatch(setLoading(false));
      return response.data;
    } catch (error) {
      dispatch(setLoading(false));
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const addNewService = createAsyncThunk(
  'labtech/addNewService',
  async (formData, { rejectWithValue, dispatch }) => {
    try {
      dispatch(setLoading(true));
      console.log('Sending request to:', `${API_BASE_URL}/LabTestServices`);
      console.log('Request data:', JSON.stringify(formData, null, 2));
      
      const response = await apiClient.post('/LabTestServices', formData);
      
      dispatch(setLoading(false));
      console.log('Response:', response.data);
      return response.data;
    } catch (error) {
      dispatch(setLoading(false));
      console.error('API Error:', error);
      if (error.response) {
        console.error('Error response data:', error.response.data);
        console.error('Error response status:', error.response.status);
        return rejectWithValue(error.response.data || error.message);
      }
      return rejectWithValue(error.message);
    }
  }
);

export const updateService = createAsyncThunk(
  'labtech/updateService',
  async ({ id, serviceData }, { rejectWithValue, dispatch }) => {
    try {
      dispatch(setLoading(true));
      const response = await apiClient.put(`/LabTestServices/${id}`, serviceData);
      dispatch(setLoading(false));
      return response.data;
    } catch (error) {
      dispatch(setLoading(false));
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const deleteService = createAsyncThunk(
  'labtech/deleteService',
  async (id, { rejectWithValue, dispatch }) => {
    try {
      dispatch(setLoading(true));
      await apiClient.delete(`/LabTestServices/${id}`);
      dispatch(setLoading(false));
      return id;
    } catch (error) {
      dispatch(setLoading(false));
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// ----- Packages -----
export const fetchPackages = createAsyncThunk(
  'labtech/fetchPackages',
  async (_, { rejectWithValue, dispatch }) => {
    try {
      dispatch(setLoading(true));
      const response = await apiClient.get('/Packages');
      dispatch(setLoading(false));
      return response.data;
    } catch (error) {
      dispatch(setLoading(false));
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const addNewPackage = createAsyncThunk(
  'labtech/addNewPackage',
  async (packageData, { rejectWithValue, dispatch }) => {
    try {
      dispatch(setLoading(true));
      const response = await apiClient.post('/Packages', packageData);
      dispatch(setLoading(false));
      return response.data;
    } catch (error) {
      dispatch(setLoading(false));
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const updatePackage = createAsyncThunk(
  'labtech/updatePackage',
  async ({ id, packageData }, { rejectWithValue, dispatch }) => {
    try {
      dispatch(setLoading(true));
      const response = await apiClient.put(`/Packages/${id}`, packageData);
      dispatch(setLoading(false));
      return response.data;
    } catch (error) {
      dispatch(setLoading(false));
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const deletePackage = createAsyncThunk(
  'labtech/deletePackage',
  async (id, { rejectWithValue, dispatch }) => {
    try {
      dispatch(setLoading(true));
      await apiClient.delete(`/Packages/${id}`);
      dispatch(setLoading(false));
      return id;
    } catch (error) {
      dispatch(setLoading(false));
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// ----- Service Groups -----
export const fetchServiceGroups = createAsyncThunk(
  'labtech/fetchServiceGroups',
  async (_, { rejectWithValue, dispatch }) => {
    try {
      dispatch(setLoading(true));
      const response = await apiClient.get('/ServiceGroups');
      dispatch(setLoading(false));
      return response.data;
    } catch (error) {
      dispatch(setLoading(false));
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const addNewServiceGroup = createAsyncThunk(
  'labtech/addNewServiceGroup',
  async (groupData, { rejectWithValue, dispatch }) => {
    try {
      dispatch(setLoading(true));
      const response = await apiClient.post('/ServiceGroups', groupData);
      dispatch(setLoading(false));
      return response.data;
    } catch (error) {
      dispatch(setLoading(false));
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const updateServiceGroup = createAsyncThunk(
  'labtech/updateServiceGroup',
  async ({ id, groupData }, { rejectWithValue, dispatch }) => {
    try {
      dispatch(setLoading(true));
      const response = await apiClient.put(`/ServiceGroups/${id}`, groupData);
      dispatch(setLoading(false));
      return response.data;
    } catch (error) {
      dispatch(setLoading(false));
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const deleteServiceGroup = createAsyncThunk(
  'labtech/deleteServiceGroup',
  async (id, { rejectWithValue, dispatch }) => {
    try {
      dispatch(setLoading(true));
      await apiClient.delete(`/ServiceGroups/${id}`);
      dispatch(setLoading(false));
      return id;
    } catch (error) {
      dispatch(setLoading(false));
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// ----- Package Services (Junction) -----
export const fetchPackageServices = createAsyncThunk(
  'labtech/fetchPackageServices',
  async (_, { rejectWithValue, dispatch }) => {
    try {
      dispatch(setLoading(true));
      const response = await apiClient.get('/PackageServices');
      dispatch(setLoading(false));
      return response.data;
    } catch (error) {
      dispatch(setLoading(false));
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const addNewPackageService = createAsyncThunk(
  'labtech/addNewPackageService',
  async (packageServiceData, { rejectWithValue, dispatch }) => {
    try {
      dispatch(setLoading(true));
      const response = await apiClient.post('/PackageServices', packageServiceData);
      dispatch(setLoading(false));
      return response.data;
    } catch (error) {
      dispatch(setLoading(false));
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const deletePackageService = createAsyncThunk(
  'labtech/deletePackageService',
  async (linkId, { rejectWithValue, dispatch }) => {
    try {
      dispatch(setLoading(true));
      await apiClient.delete(`/PackageServices/${linkId}`);
      dispatch(setLoading(false));
      return linkId;
    } catch (error) {
      dispatch(setLoading(false));
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// ----- Slice -----
const labtechSlice = createSlice({
  name: 'labtech',
  initialState: {
    services: [],
    serviceLoading: 'idle',
    serviceError: null,
    packages: [],
    packageLoading: 'idle',
    packageError: null,
    serviceGroups: [],
    groupLoading: 'idle',
    groupError: null,
    packageServices: [],
    packageServiceLoading: 'idle',
    packageServiceError: null,
  },
  reducers: {
    clearServiceError: (state) => { state.serviceError = null; },
    clearPackageError: (state) => { state.packageError = null; },
    clearGroupError: (state) => { state.groupError = null; },
    clearPackageServiceError: (state) => { state.packageServiceError = null; },
    resetLabTechState: (state) => {
      state.services = [];
      state.packages = [];
      state.serviceGroups = [];
      state.packageServices = [];
      state.serviceError = null;
      state.packageError = null;
      state.groupError = null;
      state.packageServiceError = null;
      state.serviceLoading = 'idle';
      state.packageLoading = 'idle';
      state.groupLoading = 'idle';
      state.packageServiceLoading = 'idle';
    },
  },
  extraReducers: (builder) => {
    builder
      // Services
      .addCase(fetchServices.pending, (state) => { state.serviceLoading = 'loading'; })
      .addCase(fetchServices.fulfilled, (state, action) => {
        state.serviceLoading = 'succeeded';
        state.services = action.payload;
      })
      .addCase(fetchServices.rejected, (state, action) => {
        state.serviceLoading = 'failed';
        state.serviceError = action.payload;
      })
      .addCase(addNewService.pending, (state) => { state.serviceLoading = 'loading'; })
      .addCase(addNewService.fulfilled, (state, action) => {
        state.serviceLoading = 'succeeded';
        state.services.push(action.payload);
      })
      .addCase(addNewService.rejected, (state, action) => {
        state.serviceLoading = 'failed';
        state.serviceError = action.payload;
      })
      .addCase(updateService.fulfilled, (state, action) => {
        const index = state.services.findIndex(s => s.id === action.payload.id);
        if (index !== -1) state.services[index] = action.payload;
      })
      .addCase(deleteService.fulfilled, (state, action) => {
        state.services = state.services.filter(s => s.id !== action.payload);
      })
      // Packages
      .addCase(fetchPackages.pending, (state) => { state.packageLoading = 'loading'; })
      .addCase(fetchPackages.fulfilled, (state, action) => {
        state.packageLoading = 'succeeded';
        state.packages = action.payload;
      })
      .addCase(fetchPackages.rejected, (state, action) => {
        state.packageLoading = 'failed';
        state.packageError = action.payload;
      })
      .addCase(addNewPackage.fulfilled, (state, action) => { state.packages.push(action.payload); })
      .addCase(updatePackage.fulfilled, (state, action) => {
        const index = state.packages.findIndex(p => p.id === action.payload.id);
        if (index !== -1) state.packages[index] = action.payload;
      })
      .addCase(deletePackage.fulfilled, (state, action) => {
        state.packages = state.packages.filter(p => p.id !== action.payload);
      })
      // Service Groups
      .addCase(fetchServiceGroups.pending, (state) => { state.groupLoading = 'loading'; })
      .addCase(fetchServiceGroups.fulfilled, (state, action) => {
        state.groupLoading = 'succeeded';
        state.serviceGroups = action.payload;
      })
      .addCase(fetchServiceGroups.rejected, (state, action) => {
        state.groupLoading = 'failed';
        state.groupError = action.payload;
      })
      .addCase(addNewServiceGroup.fulfilled, (state, action) => { state.serviceGroups.push(action.payload); })
      .addCase(updateServiceGroup.fulfilled, (state, action) => {
        const index = state.serviceGroups.findIndex(g => g.id === action.payload.id);
        if (index !== -1) state.serviceGroups[index] = action.payload;
      })
      .addCase(deleteServiceGroup.fulfilled, (state, action) => {
        state.serviceGroups = state.serviceGroups.filter(g => g.id !== action.payload);
      })
      // Package Services
      .addCase(fetchPackageServices.pending, (state) => { state.packageServiceLoading = 'loading'; })
      .addCase(fetchPackageServices.fulfilled, (state, action) => {
        state.packageServiceLoading = 'succeeded';
        state.packageServices = action.payload;
      })
      .addCase(fetchPackageServices.rejected, (state, action) => {
        state.packageServiceLoading = 'failed';
        state.packageServiceError = action.payload;
      })
      .addCase(addNewPackageService.fulfilled, (state, action) => {
        state.packageServices.push(action.payload);
      })
      .addCase(deletePackageService.fulfilled, (state, action) => {
        state.packageServices = state.packageServices.filter(ps => ps.id !== action.payload);
      });
  },
});

export const {
  clearServiceError,
  clearPackageError,
  clearGroupError,
  clearPackageServiceError,
  resetLabTechState,
} = labtechSlice.actions;

export default labtechSlice.reducer;